// 상단 — 60일 smoothed Top3 라인 차트 (P0, PRD §5.7.2 권위).
// recharts/echarts 미도입 — SVG fallback (SectorTaxonomy BalanceChartsTab 패턴 차용).

import { memo, useMemo } from 'react';
import type { SectorHistoryRow } from './leadingSectors.types';
import { sectorColor } from './leadingSectors.types';

interface Props {
  rows: SectorHistoryRow[];
  isLoading?: boolean;
}

interface Series {
  sector: string;
  color: string;
  points: { date: string; score: number }[];
}

// SVG viewBox — 차트 영역 좌우 모두 차도록 (스크롤 없음).
const VIEW_W = 1100;
const VIEW_H = 320;
const PAD = { top: 16, right: 16, bottom: 32, left: 48 };

export const SmoothedTop3LineChart = memo(function SmoothedTop3LineChart({
  rows,
  isLoading,
}: Props): JSX.Element {
  // 섹터별 series 묶기. 일자는 ASC.
  const series: Series[] = useMemo(() => {
    if (!rows.length) return [];
    const bySector = new Map<string, { date: string; score: number }[]>();
    for (const r of rows) {
      if (!r.is_smoothed_lead) continue;
      if (r.score_daily == null) continue;
      const arr = bySector.get(r.sector_name) ?? [];
      arr.push({ date: r.trading_date, score: Number(r.score_daily) });
      bySector.set(r.sector_name, arr);
    }
    return Array.from(bySector.entries())
      .map(([sector, points]) => {
        points.sort((a, b) => a.date.localeCompare(b.date));
        return { sector, color: sectorColor(sector), points };
      })
      // Top3 라인이지만 60일 누적으로 보면 N>3 가능. 출연 빈도순 정렬.
      .sort((a, b) => b.points.length - a.points.length);
  }, [rows]);

  const { allDates, yMin, yMax } = useMemo(() => {
    const datesSet = new Set<string>();
    let mn = Infinity;
    let mx = -Infinity;
    for (const s of series) {
      for (const p of s.points) {
        datesSet.add(p.date);
        if (p.score < mn) mn = p.score;
        if (p.score > mx) mx = p.score;
      }
    }
    const allDatesArr = Array.from(datesSet).sort();
    if (!Number.isFinite(mn)) {
      mn = 0;
      mx = 1;
    } else if (mn === mx) {
      mn = mn - 1;
      mx = mx + 1;
    }
    return { allDates: allDatesArr, yMin: mn, yMax: mx };
  }, [series]);

  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom;

  const xOf = (date: string): number => {
    if (allDates.length === 0) return PAD.left;
    if (allDates.length === 1) return PAD.left + plotW / 2;
    const idx = allDates.indexOf(date);
    return PAD.left + (idx / (allDates.length - 1)) * plotW;
  };

  const yOf = (score: number): number => {
    const span = yMax - yMin || 1;
    return PAD.top + plotH - ((score - yMin) / span) * plotH;
  };

  const pathFor = (s: Series): string =>
    s.points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(p.date).toFixed(2)} ${yOf(p.score).toFixed(2)}`)
      .join(' ');

  // X 축 tick — 양 끝 + 중간 약 5개.
  const xTicks = useMemo(() => {
    if (allDates.length === 0) return [];
    if (allDates.length <= 6) return allDates;
    const n = 6;
    return Array.from({ length: n }, (_, i) => {
      const idx = Math.round((i * (allDates.length - 1)) / (n - 1));
      return allDates[idx];
    });
  }, [allDates]);

  // Y 축 tick — 5단계.
  const yTicks = useMemo(() => {
    const n = 5;
    return Array.from({ length: n }, (_, i) => yMin + ((yMax - yMin) * i) / (n - 1));
  }, [yMin, yMax]);

  return (
    <div data-testid="ls-linechart-wrapper">
      <svg
        className="ls-linechart"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="60일 smoothed Top3 라인 차트"
      >
        {/* Y grid */}
        {yTicks.map((t, i) => (
          <g key={`yg-${i}`}>
            <line
              className="ls-linechart__grid"
              x1={PAD.left}
              y1={yOf(t)}
              x2={PAD.left + plotW}
              y2={yOf(t)}
            />
            <text
              className="ls-linechart__tick"
              x={PAD.left - 6}
              y={yOf(t)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {t.toFixed(1)}
            </text>
          </g>
        ))}
        {/* X axis */}
        <line
          className="ls-linechart__axis"
          x1={PAD.left}
          y1={PAD.top + plotH}
          x2={PAD.left + plotW}
          y2={PAD.top + plotH}
        />
        <line
          className="ls-linechart__axis"
          x1={PAD.left}
          y1={PAD.top}
          x2={PAD.left}
          y2={PAD.top + plotH}
        />
        {xTicks.map((d) => (
          <text
            key={`xt-${d}`}
            className="ls-linechart__tick"
            x={xOf(d)}
            y={PAD.top + plotH + 16}
            textAnchor="middle"
          >
            {d.slice(5)}
          </text>
        ))}
        {/* lines */}
        {series.map((s) => (
          <path
            key={s.sector}
            d={pathFor(s)}
            className="ls-linechart__line"
            style={{ stroke: s.color }}
          />
        ))}
        {/* point markers — 점 1개 시계열에서도 시각 확인 가능하도록 (이슈 1 영구 개선) */}
        {series.map((s) => (
          <g key={`m-${s.sector}`} data-testid={`ls-linechart-markers-${s.sector}`}>
            {s.points.map((p) => (
              <circle
                key={`${s.sector}-${p.date}`}
                cx={xOf(p.date).toFixed(2)}
                cy={yOf(p.score).toFixed(2)}
                r={2.5}
                className="ls-linechart__marker"
                style={{ fill: s.color }}
              />
            ))}
          </g>
        ))}
        {/* loading / empty */}
        {isLoading && (
          <text
            x={VIEW_W / 2}
            y={VIEW_H / 2}
            textAnchor="middle"
            className="ls-linechart__tick"
          >
            불러오는 중…
          </text>
        )}
        {!isLoading && series.length === 0 && (
          <text
            x={VIEW_W / 2}
            y={VIEW_H / 2}
            textAnchor="middle"
            className="ls-linechart__tick"
          >
            데이터 없음
          </text>
        )}
      </svg>

      {/* 범례 */}
      {series.length > 0 && (
        <div className="ls-legend">
          {series.map((s) => (
            <span key={s.sector} className="ls-legend__item">
              <span
                className="ls-legend__swatch"
                style={{ background: s.color }}
                aria-hidden
              />
              {s.sector}
              <span className="ls-muted">·{s.points.length}일</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
});
